import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_URL}`;

// Get token from localStorage
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication token not found. Please log in again.');
  }
  return { Authorization: `Bearer ${token}` };
};

// Fetch invoices for a specific student
export const fetchStudentInvoices = async (studentId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/invoices?studentId=${studentId}`,
      { headers: getAuthHeader() }
    );
    
    return response.data.invoices || [];
  } catch (error) {
    console.error("Error fetching invoices:", error);
    throw error;
  }
};

// Create a new invoice
export const createInvoice = async (invoiceData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/invoices`,
      invoiceData,
      { headers: getAuthHeader() }
    );
    
    return response.data;
  } catch (error) {
    console.error("Error creating invoice:", error);
    throw error;
  }
};

// Process a student payment and create an invoice
export const processPayment = async (student, paymentData, receiptDetails) => {
  try {
    if (!student || typeof student !== 'object') {
      throw new Error("Missing or invalid student data");
    }

    if (!paymentData || typeof paymentData !== 'object') {
      throw new Error("Missing or invalid payment data");
    }

    if (!paymentData.paymentAmount || isNaN(parseFloat(paymentData.paymentAmount))) {
      throw new Error("Valid payment amount is required");
    }

    const studentId = String(student.studentId);
    const currentFeesCollected = parseFloat(student.feesCollected) || 0;
    const paymentAmount = parseFloat(paymentData.paymentAmount);

    // Parse pending fees
    let pendingFeesFields = [
      { key: 'pendingFees', amount: parseFloat(student.pendingFees) || 0, dateKey: 'pendingFeesDate', newAmount: 0 },
      { key: 'pendingFees2', amount: parseFloat(student.pendingFees2) || 0, dateKey: 'pendingFeesDate2', newAmount: 0 },
      { key: 'pendingFees3', amount: parseFloat(student.pendingFees3) || 0, dateKey: 'pendingFeesDate3', newAmount: 0 },
      { key: 'pendingFees4', amount: parseFloat(student.pendingFees4) || 0, dateKey: 'pendingFeesDate4', newAmount: 0 }
    ];

    let remainingPayment = paymentAmount;

    for (let item of pendingFeesFields) {
      if (remainingPayment >= item.amount) {
        remainingPayment -= item.amount;
        item.newAmount = 0;
      } else {
        item.newAmount = item.amount - remainingPayment;
        remainingPayment = 0;
      }
    }

    const newFeesCollected = currentFeesCollected + paymentAmount;

    const updatedData = {
      feesCollected: newFeesCollected.toFixed(2),
      modeOfPayment: paymentData.modeOfPayment,
      referenceNo: paymentData.referenceNo || "N/A",
    };

    // Add each updated pending amount and date (null if 0)
    for (let item of pendingFeesFields) {
      updatedData[item.key] = item.newAmount.toFixed(2);
      updatedData[item.dateKey] = item.newAmount === 0 ? null : paymentData[item.dateKey];
    }

    const updateResponse = await axios.put(
      `${API_BASE_URL}/registrations/${studentId}`,
      updatedData,
      { headers: getAuthHeader() }
    );

    if (updateResponse.status !== 200) {
      throw new Error("Failed to update payment information.");
    }

    const employeeResponse = await axios.get(
      `${API_BASE_URL}/regdetail`,
      { headers: getAuthHeader() }
    );

    const currentEmployee = employeeResponse.data;

    if (!currentEmployee || !currentEmployee.emp_id) {
      throw new Error("Employee not found for the logged-in user.");
    }

    const empId = currentEmployee.emp_id;

    const invoiceData = {
      EmpId: empId,
      studentId: studentId,
      receipt_no: receiptDetails.receiptNumber,
      registrationPaymentMode: paymentData.modeOfPayment,
      registrationReferenceNo:
        paymentData.modeOfPayment === 'Cash' ? '' : (paymentData.referenceNo || "N/A"),
      amount: paymentData.modeOfPayment === 'Cash' ? 0 : receiptDetails.finalAmount,
      cgst: paymentData.modeOfPayment === 'Cash' ? 0 : receiptDetails.cgstAmount,
      sgst: paymentData.modeOfPayment === 'Cash' ? 0 : receiptDetails.sgstAmount,
      paymentInstallment: 1,
      bank: paymentData.bank,
      paidAmount: paymentAmount,
      paymentDate: paymentData.paymentDate,
    };

    const invoiceResponse = await axios.post(
      `${API_BASE_URL}/invoices`,
      invoiceData,
      { headers: getAuthHeader() }
    );

    if (![200, 201].includes(invoiceResponse.status)) {
      throw new Error("Failed to create invoice.");
    }

    return {
      updatedStudent: { ...student, ...updatedData },
      invoice: invoiceResponse.data
    };
  } catch (error) {
    console.error("Error processing payment:", error.message);
    throw error;
  }
};




